import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SectionHeading from "@/components/base/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getMediaById, getRelated, typeLabel } from "@/utils/media";

export default function Detail() {
  const { id } = useParams();
  const item = id ? getMediaById(id) : undefined;
  const { toggle, isFavorite } = useFavorites();
  const [copied, setCopied] = useState(false);

  usePageMeta(
    item ? `${item.title} - 影视详情 - 茶杯狐影视` : "影视详情 - 茶杯狐影视",
    item
      ? `${item.title}：${typeLabel[item.type]}，${item.year}年${item.region}作品，${item.genres.join("、")}题材，评分${item.rating}。${item.synopsis}`
      : "茶杯狐影视影视作品详情页。",
  );

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  if (!item) {
    return (
      <div className="mx-auto flex max-w-page flex-col items-center justify-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-600">
          <i className="ri-film-line text-3xl" aria-hidden="true" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          作品不存在或已下架
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          该影视作品可能已被移除，去首页看看其他精彩内容吧。
        </p>
        <Link
          to="/"
          className="mt-6 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(item.id);
  const related = getRelated(item, 4);

  const handleShare = async () => {
    try {
      const url = window.location.href;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement("input");
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const metaItems = [
    { label: "类型", value: typeLabel[item.type] },
    { label: "地区", value: item.region },
    { label: "上映年份", value: `${item.year}` },
    { label: "时长", value: item.duration },
    { label: "导演", value: item.director },
    { label: "主演", value: item.actors.join(" / ") },
  ];

  return (
    <div className="w-full">
      {/* 主图区 */}
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={item.backdrop}
          alt={`${item.title}主图`}
          title={`${item.title} - 影视详情`}
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/50" />
        <div className="relative z-10 mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
          <nav className="flex items-center gap-1.5 text-xs text-primary-200">
            <Link to="/" className="transition-colors hover:text-accent-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <Link
              to={`/category/${item.type}`}
              className="transition-colors hover:text-accent-400"
            >
              {typeLabel[item.type]}
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span className="text-background-50">{item.title}</span>
          </nav>

          <div className="mt-6 flex flex-col gap-6 sm:flex-row sm:gap-8">
            <div className="h-64 w-44 shrink-0 overflow-hidden rounded-lg border border-background-50/10 sm:h-72 sm:w-48">
              <img
                src={item.cover}
                alt={`${item.title}海报`}
                title={`${item.title} - 影视详情`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="flex flex-1 flex-col">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2.5 py-0.5 text-xs font-bold text-accent-950">
                  {typeLabel[item.type]}
                </span>
                <span className="flex items-center gap-1 rounded-md bg-background-50/15 px-2.5 py-0.5 text-xs text-background-100 backdrop-blur-sm">
                  <i className="ri-star-fill text-accent-400" aria-hidden="true" />
                  {item.rating.toFixed(1)} 分
                </span>
                <span className="rounded-md bg-background-50/15 px-2.5 py-0.5 text-xs text-background-100 backdrop-blur-sm">
                  更新于 {item.updatedAt}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-4xl">
                {item.title}
              </h1>

              <div className="mt-3 flex flex-wrap gap-2">
                {item.genres.map((genre) => (
                  <span
                    key={genre}
                    className="rounded-md border border-background-50/20 px-2.5 py-0.5 text-xs text-background-100"
                  >
                    {genre}
                  </span>
                ))}
              </div>

              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
                {item.synopsis}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggle(item.id)}
                  className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-semibold transition-colors ${
                    favorited
                      ? "bg-accent-500 text-accent-950 hover:bg-accent-600"
                      : "border border-background-50/30 bg-background-50/10 text-background-50 backdrop-blur-sm hover:bg-background-50/20"
                  }`}
                >
                  <i
                    className={favorited ? "ri-heart-fill" : "ri-heart-line"}
                    aria-hidden="true"
                  />
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 bg-background-50/10 px-5 py-2.5 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
                >
                  <i
                    className={copied ? "ri-check-line" : "ri-share-line"}
                    aria-hidden="true"
                  />
                  {copied ? "链接已复制" : "分享链接"}
                </button>
                <Link
                  to={`/category/${item.type}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 bg-background-50/10 px-5 py-2.5 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
                >
                  <i className="ri-layout-grid-line" aria-hidden="true" />
                  更多{typeLabel[item.type]}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHeading title="剧情简介" icon="ri-file-text-line" />
            <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              {item.storyline.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-sm leading-loose text-foreground-700 md:text-base"
                >
                  {paragraph}
                </p>
              ))}
              <div className="flex flex-wrap gap-2 pt-2">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <SectionHeading title="作品信息" icon="ri-information-line" />
            <dl className="divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {metaItems.map((meta) => (
                <div
                  key={meta.label}
                  className="flex gap-4 px-4 py-3.5 text-sm"
                >
                  <dt className="w-20 shrink-0 text-foreground-400">
                    {meta.label}
                  </dt>
                  <dd className="flex-1 text-foreground-800">{meta.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-5 rounded-lg border border-accent-200 bg-accent-50 p-4">
              <p className="flex items-start gap-2 text-xs leading-relaxed text-accent-900">
                <i className="ri-information-line mt-0.5 shrink-0" aria-hidden="true" />
                本站内容均为演示示例，不提供任何真实播放、下载或会员服务。
              </p>
            </div>
          </aside>
        </div>

        {related.length > 0 ? (
          <section className="mt-14">
            <SectionHeading
              title="相关推荐"
              subtitle="根据类型与标签为你匹配"
              icon="ri-magic-line"
              moreTo={`/category/${item.type}`}
            />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {related.map((rel, index) => (
                <MovieCard key={rel.id} item={rel} index={index} />
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}